/** @format */
import {
  Button,
  ButtonSize,
  ButtonType,
  ButtonVariant,
  TextInput,
} from "@roadflex/react-components-www";
import { LandingPageFormSubmissionValues } from "@roadflex/types";
import { handleGoogleReCaptchaSubmit, phoneFormatter } from "@roadflex/web-lib";
import { FormikErrors, FormikTouched } from "formik";
import getConfig from "next/config";
import Image from "next/image";
import { useRouter } from "next/router";
import { FocusEvent, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";

interface BigBannerProps {
  heading: string;
  heading2?: string;
  details: string;
  details2?: string;
  includeCallToAction?: boolean;
  vertical?: boolean;
  contactUs?: boolean;
  handleChange?: (e?: React.FormEvent<HTMLFormElement>) => void;
  handleSubmit?: (e?: React.FormEvent<HTMLFormElement>) => void;
  handleBlur?: (e: FocusEvent<HTMLInputElement, Element>) => void;
  isSubmitting?: boolean;
  setFieldValue?: (field: string, value: string) => void;
  errors?: FormikErrors<LandingPageFormSubmissionValues>;
  touched?: FormikTouched<LandingPageFormSubmissionValues>;
  setFieldTouched?: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean,
  ) => void;
  values?: LandingPageFormSubmissionValues;
}
export function BigBanner(props: BigBannerProps) {
  const {
    heading,
    details,
    heading2,
    details2,
    includeCallToAction = false,
    vertical = false,
    contactUs = false,
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    errors,
    isSubmitting,
    touched,
    setFieldValue,
    setFieldTouched,
  } = props;
  const router = useRouter();
  const goToSignupPage = () => {
    router.push({
      pathname: "/signup",
    });
  };
  const captchaRef = useRef<ReCAPTCHA>(null);
  const { publicRuntimeConfig } = getConfig();
  const { RECAPTCHA_SITE_KEY, NEXT_PUBLIC_CDN_URL } = publicRuntimeConfig;
  const images = [
    {
      imageLink: `${NEXT_PUBLIC_CDN_URL}/contact-us/images/getapp_reviews.png`,
      width: "150",
      height: "100",
    },
    {
      imageLink: `${NEXT_PUBLIC_CDN_URL}/contact-us/images/capterra.png`,
      width: "150",
      height: "120",
    },
    {
      imageLink: `${NEXT_PUBLIC_CDN_URL}/contact-us/images/getapp_functionality.png`,
      width: "150",
      height: "120",
    },
    {
      imageLink: `${NEXT_PUBLIC_CDN_URL}/contact-us/images/best_customer_support.png`,
      width: "150",
      height: "120",
    },
  ];
  return (
    <div
      className={`${
        contactUs
          ? `block w-full text-center bg-darkblue py-14 md:py-28`
          : vertical
          ? `w-full h-full text-center text-white py-14 md:py-28 bg-cover bg-banner-header justify-center flex`
          : `block w-full h-full text-center text-white py-14 md:py-28 bg-cover bg-banner-header`
      }`}
    >
      <div
        className={`${
          vertical
            ? `flex flex-col justify-center text-center animate-slidein h-full`
            : `container md:my-24 min-h-fit animate-slidein`
        }`}
      >
        <div className="mb-3 text-2xl font-semibold md:text-4xl">
          <h1 className={`${contactUs ? `text-white` : `px-4 lg:px-0`}`}>
            {heading}
          </h1>
          {heading2 && <div className="">{heading2}</div>}
        </div>
        <div className="px-12 py-3 text-base lg:px-20 md:text-xl">
          {details}
        </div>
        {details2 && (
          <div className="px-12 py-3 text-base font-medium lg:px-20 md:text-xl">
            {details2}
          </div>
        )}
        <div className="flex justify-center mt-4">
          {includeCallToAction && (
            <Button
              variant={ButtonVariant.Orange}
              onClick={goToSignupPage}
              className="!px-4 sm:!px-8 font-medium"
              size={ButtonSize.Large}
            >
              Apply Now
            </Button>
          )}
        </div>
        {contactUs === true && (
          <>
            <form
              className="m-8"
              onSubmit={(event) => {
                handleGoogleReCaptchaSubmit(event, captchaRef, handleSubmit);
              }}
            >
              <div className="grid w-full grid-cols-12 gap-2 text-black md:gap-3">
                <div className="relative col-span-12 md:col-span-6">
                  <TextInput
                    name="name"
                    value={values.name}
                    onChange={(event) =>
                      setFieldValue("name", event.target.value)
                    }
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    errorMessage={errors.name}
                    touched={touched.name}
                    variant="responsive"
                    placeholder="First and Last name"
                    required
                  ></TextInput>
                </div>
                <div className="relative col-span-12 md:col-span-6">
                  <TextInput
                    name="companyName"
                    value={values.companyName}
                    onChange={(event) =>
                      setFieldValue("companyName", event.target.value)
                    }
                    onBlur={handleBlur}
                    error={touched.companyName && Boolean(errors.companyName)}
                    errorMessage={errors.companyName}
                    touched={touched.companyName}
                    variant="responsive"
                    required
                    placeholder="Company Name"
                  ></TextInput>
                </div>
                <div className="relative col-span-12 md:col-span-6">
                  <TextInput
                    name="email"
                    value={values.email}
                    onChange={(event) =>
                      setFieldValue("email", event.target.value)
                    }
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    errorMessage={errors.email}
                    touched={touched.email}
                    variant="responsive"
                    required
                    placeholder="Business Email"
                  ></TextInput>
                </div>
                <div className="relative col-span-12 md:col-span-6">
                  <TextInput
                    name="phoneNumber"
                    value={values.phoneNumber}
                    onChange={(event) =>
                      setFieldValue(
                        "phoneNumber",
                        phoneFormatter(event?.target?.value),
                      )
                    }
                    onBlur={handleBlur}
                    error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                    errorMessage={errors.phoneNumber}
                    touched={touched.phoneNumber}
                    variant="responsive"
                    required
                    placeholder="Phone Number"
                  ></TextInput>
                </div>
              </div>
              <ReCAPTCHA
                className="invisible recaptcha"
                sitekey={RECAPTCHA_SITE_KEY}
                ref={captchaRef}
                size="invisible"
              />
              <div className="flex items-center justify-center w-full mt-8 md:mt-8">
                <Button
                  type={ButtonType.Submit}
                  size={ButtonSize.Large}
                  variant={ButtonVariant.Brown}
                  className="sm:!py-3 w-full"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  I want to get contacted
                </Button>
              </div>
            </form>
            <div className="flex justify-center mt-8 2xl:mt-16">
              <div className="justify-between hidden w-2/3 h-full gap-6 md:flex md:flex-nowrap md:flex-row">
                {images.length > 0 &&
                  images.map((image, idx) => (
                    <div
                      key={idx}
                      className="items-center hidden max-h-5 sm:block basis-1/4 animate-slidein"
                    >
                      <div className="w-full h-full mb-4">
                        <Image
                          src={image.imageLink}
                          layout="responsive"
                          objectFit="contain"
                          width={image.width}
                          priority={true}
                          height={image.height}
                          quality={80}
                          alt="RoadFlex Reviews"
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BigBanner;
